/* Photo attachment for the composers.
 *
 * Uploads happen AS SOON AS a file is picked, while the person is still
 * typing, so pressing Post is instant. Each file gets its own row in local
 * state with a status, because one failing must not take the others with it.
 *
 * The preview comes from a local object URL rather than waiting for the
 * server's, so the image appears the moment it is chosen. Those URLs hold a
 * reference to the file until revoked, which is why every removal path does.
 */
import { useEffect, useRef, useState } from 'react'
import { AlertCircle, ImagePlus, Loader2, Play, X } from 'lucide-react'
import { api, putFile } from '@/api/client'
import { useUploadImage } from '@/api/queries'
import { cn } from '@/lib/utils'

/** One picked file, and where it got to. */
export interface Attachment {
  /** Local key. Not the server id — that arrives only once uploaded. */
  key: string
  previewUrl: string
  status: 'uploading' | 'done' | 'error'
  kind: 'image' | 'video'
  /** How much of a video has gone up, 0–1. Photos are small enough not to
   *  need it. */
  progress?: number
  /** Set once the server has it. This is what gets sent on publish. */
  mediaId?: string
  error?: string
}

/* A testimony, not a service. Checked here as well as on the server so a
   long recording is refused BEFORE twenty minutes of uploading, not after. */
const MAX_VIDEO_SECONDS = 5 * 60
const MAX_VIDEO_BYTES = 500 * 1024 * 1024

/** How long a video runs, read from the file itself without uploading it. */
function videoSeconds(url: string): Promise<number> {
  return new Promise((resolve) => {
    const el = document.createElement('video')
    el.preload = 'metadata'
    el.onloadedmetadata = () => resolve(el.duration || 0)
    // A format this browser cannot read locally may still convert fine on
    // the server, so an unreadable length is not a refusal.
    el.onerror = () => resolve(0)
    el.src = url
  })
}

/** Start, send, and confirm one video. The API never sees the bytes. */
async function uploadVideo(
  file: File,
  onProgress: (fraction: number) => void,
): Promise<string> {
  const started = await api.post<{ id: string; upload_url: string }>(
    '/media/videos',
    { byte_size: file.size, content_type: file.type || 'video/mp4' },
  )
  await putFile(started.upload_url, file, onProgress)
  await api.post(`/media/${started.id}/uploaded`)
  return started.id
}

/** Four is what fits a card without becoming a gallery of its own. */
export const MAX_ATTACHMENTS = 4

/** @param taken photos the caller ALREADY holds outside this hook — the ones
 *  an edit kept from the post. Without it the cap counts only newly picked
 *  files, and a post with three photos could take four more. */
export function usePhotoAttachments(taken = 0) {
  const [items, setItems] = useState<Attachment[]>([])
  const upload = useUploadImage()
  const capacity = Math.max(0, MAX_ATTACHMENTS - taken)

  // Object URLs are held until revoked. Without this, picking and discarding
  // photos leaks a file's worth of memory each time.
  const itemsRef = useRef(items)
  itemsRef.current = items
  useEffect(
    () => () => {
      itemsRef.current.forEach((i) => URL.revokeObjectURL(i.previewUrl))
    },
    [],
  )

  function fail(key: string, error: string) {
    setItems((prev) =>
      prev.map((i) => (i.key === key ? { ...i, status: 'error', error } : i)),
    )
  }

  /* ONE VIDEO, ON ITS OWN. A post carries photos or a single video — a
     video squeezed into a grid of photos is one nobody can watch. */
  async function addVideo(file: File) {
    const key = `${file.name}-${file.size}-${Math.random()}`
    const previewUrl = URL.createObjectURL(file)
    setItems((prev) => [
      ...prev,
      { key, previewUrl, status: 'uploading', kind: 'video', progress: 0 },
    ])

    if (items.length > 0) {
      return fail(key, 'A post can have photos or one video, not both.')
    }
    if (file.size > MAX_VIDEO_BYTES) {
      return fail(key, 'Videos must be under 500 MB.')
    }
    if ((await videoSeconds(previewUrl)) > MAX_VIDEO_SECONDS + 1) {
      return fail(key, 'Videos can be up to 5 minutes long.')
    }

    try {
      const mediaId = await uploadVideo(file, (progress) =>
        setItems((prev) =>
          prev.map((i) => (i.key === key ? { ...i, progress } : i)),
        ),
      )
      setItems((prev) =>
        prev.map((i) =>
          i.key === key ? { ...i, status: 'done', mediaId, progress: 1 } : i,
        ),
      )
    } catch (err) {
      fail(key, (err as Error)?.message ?? 'Upload failed')
    }
  }

  function add(files: FileList | File[] | null) {
    if (!files?.length) return
    const all = Array.from(files)
    const video = all.find((f) => f.type.startsWith('video/'))
    if (video) {
      void addVideo(video)
      return
    }
    if (items.some((i) => i.kind === 'video')) return
    const picked = all.slice(0, capacity - items.length)

    for (const file of picked) {
      const key = `${file.name}-${file.size}-${Math.random()}`
      const previewUrl = URL.createObjectURL(file)
      setItems((prev) => [
        ...prev,
        { key, previewUrl, status: 'uploading', kind: 'image' },
      ])

      upload.mutate(file, {
        onSuccess: (media) =>
          setItems((prev) =>
            prev.map((i) =>
              i.key === key ? { ...i, status: 'done', mediaId: media.id } : i,
            ),
          ),
        onError: (err) =>
          setItems((prev) =>
            prev.map((i) =>
              i.key === key
                ? {
                    ...i,
                    status: 'error',
                    error: (err as Error)?.message ?? 'Upload failed',
                  }
                : i,
            ),
          ),
      })
    }
  }

  function remove(key: string) {
    setItems((prev) => {
      prev.filter((i) => i.key === key).forEach((i) =>
        URL.revokeObjectURL(i.previewUrl),
      )
      return prev.filter((i) => i.key !== key)
    })
  }

  function reset() {
    items.forEach((i) => URL.revokeObjectURL(i.previewUrl))
    setItems([])
  }

  return {
    items,
    add,
    remove,
    reset,
    /** Something worth posting: a refused or failed file is not. Counting
     *  it enabled Post on a draft holding nothing but an error. */
    hasAny: items.some((i) => i.status !== 'error'),
    /** Only the ones that actually landed — a failed upload has no id. */
    mediaIds: items.filter((i) => i.mediaId).map((i) => i.mediaId!),
    /** Publishing while an upload is in flight would silently drop it. */
    isUploading: items.some((i) => i.status === 'uploading'),
    /** How far a video upload has got, for the Post button, or null. */
    videoProgress:
      items.find((i) => i.kind === 'video' && i.status === 'uploading')
        ?.progress ?? null,
    /** A video fills the post on its own. */
    isFull: items.length >= capacity || items.some((i) => i.kind === 'video'),
  }
}

export function PhotoGrid({
  items,
  onRemove,
}: {
  items: Attachment[]
  onRemove: (key: string) => void
}) {
  if (!items.length) return null

  return (
    <div
      className={cn(
        'grid gap-s2',
        items.length === 1 ? 'grid-cols-1' : 'grid-cols-2',
      )}
    >
      {items.map((item) => (
        <div
          key={item.key}
          className="relative overflow-hidden rounded bg-surface-2"
        >
          {item.kind === 'video' ? (
            <video
              src={item.previewUrl}
              muted
              playsInline
              preload="metadata"
              className={cn(
                'max-h-[320px] w-full bg-black object-contain',
                item.status === 'uploading' && 'opacity-60',
              )}
            />
          ) : (
            <img
              src={item.previewUrl}
              alt=""
              className={cn(
                'w-full object-cover',
                items.length === 1 ? 'max-h-[320px]' : 'h-[150px]',
                item.status === 'uploading' && 'opacity-60',
              )}
            />
          )}

          {item.kind === 'video' && item.status === 'done' ? (
            <div className="absolute inset-0 grid place-items-center">
              <Play className="h-8 w-8 text-white drop-shadow" aria-hidden />
            </div>
          ) : null}

          {item.status === 'uploading' ? (
            <div className="absolute inset-0 grid place-items-center">
              {item.kind === 'video' ? (
                /* A bar, not a spinner: a large video takes minutes, and a
                   spinner that has turned for two of them reads as stuck. */
                <div className="w-3/4 text-center">
                  <div className="h-1.5 overflow-hidden rounded-pill bg-white/40">
                    <div
                      className="h-full bg-white transition-[width]"
                      style={{ width: `${Math.round((item.progress ?? 0) * 100)}%` }}
                    />
                  </div>
                  <p className="mt-1 text-sm font-semibold text-white drop-shadow">
                    Uploading {Math.round((item.progress ?? 0) * 100)}%
                  </p>
                </div>
              ) : (
                <Loader2
                  className="h-6 w-6 animate-spin text-white drop-shadow"
                  aria-label="Uploading"
                />
              )}
            </div>
          ) : null}

          {item.status === 'error' ? (
            <div className="absolute inset-0 grid place-items-center bg-danger-soft/90 p-s2 text-center">
              <div>
                <AlertCircle
                  className="mx-auto h-5 w-5 text-danger"
                  aria-hidden
                />
                <p className="mt-1 text-sm font-semibold text-danger">
                  {item.error}
                </p>
              </div>
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => onRemove(item.key)}
            aria-label={item.kind === 'video' ? 'Remove video' : 'Remove photo'}
            className="absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-pill bg-black/60 text-white transition-colors hover:bg-black/80"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  )
}

/** The "Add photos" control. A styled label wrapping a hidden input — the
 *  native file input cannot be styled, but a label triggers it and can be. */
export function PhotoButton({
  onPick,
  disabled,
  label = 'Add photos or a video',
  accept = 'image/*,video/*',
  compact = false,
  children,
}: {
  onPick: (files: FileList | null) => void
  disabled?: boolean
  label?: string
  /** Narrowed to photos where a video would end up beside them. */
  accept?: string
  /** Just the icon, as one of a row — the label becomes its tooltip. */
  compact?: boolean
  /** In place of the compact button's own look: what to show inside it. */
  children?: React.ReactNode
}) {
  const id = useRef(`photo-${Math.random().toString(36).slice(2)}`).current

  return (
    <>
      <input
        id={id}
        type="file"
        accept={accept}
        multiple
        className="sr-only"
        disabled={disabled}
        onChange={(e) => {
          onPick(e.target.files)
          // Cleared so picking the SAME file twice still fires a change event.
          e.target.value = ''
        }}
      />
      {children ? (
        <label
          htmlFor={id}
          title={label}
          className={cn(
            'flex items-center gap-1.5 rounded-pill px-s3 py-1.5 text-sm font-semibold text-ink-2 transition-colors',
            disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer hover:bg-hover',
          )}
        >
          {children}
        </label>
      ) : compact ? (
        <label
          htmlFor={id}
          title={label}
          aria-label={label}
          className={cn(
            'grid h-9 w-9 place-items-center rounded-pill transition-colors',
            disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer hover:bg-hover',
          )}
        >
          <ImagePlus className="h-[22px] w-[22px] text-success" aria-hidden />
        </label>
      ) : (
      <label
          htmlFor={id}
          className={cn(
            'flex cursor-pointer items-center justify-center gap-s2 rounded border border-dashed border-line px-s4 py-s3 text-base font-semibold text-ink-2 transition-colors',
            disabled
              ? 'cursor-not-allowed opacity-50'
              : 'hover:border-brand hover:bg-brand-softer hover:text-brand',
          )}
          title={disabled ? `Up to ${MAX_ATTACHMENTS} photos` : undefined}
        >
          <ImagePlus className="h-5 w-5" aria-hidden />
          {label}
        </label>
      )}
    </>
  )
}
